"use client";

import { getUserAuctionItems } from "@/actions/auctions";
import { auctionFilterProps, AuctionItem } from "@/types/auction";
import React, { use, useEffect, useMemo, useState } from "react";
import useSWR, { mutate } from "swr";
import { useAuctionFilter } from "@/hooks/useAuctionFilter";
import AuctionBarActionsPanelMyPages from "@/components/Auctions/AuctionBarFilter/FilterPanelMyPages";
import { getRecentItemBidsByItemIds } from "@/actions/bids";
import useUserStore from "@/stores/useUserStore";
import { MD5 } from "crypto-js";
import { EXTRA_BID_TIME, REVALIDATE_USER_AUCTIONS_TIME } from "@/lib/auctions";
import AuctionListItems from "@/components/Auctions/AuctionItem/AuctionListItems";
import { getSwedishDate } from "@/lib/utils";
import { SocketProvider } from "@/components/WebSocket/SocketProvider";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

export const defaultFilterPropsMyPagesAuctionItem = {
  pagination: {
    size: 25,
    page: 1,
  },
  sorting: {
    options: [
      {
        id: "itemEndTimeAsc",
        queryKey: "itemEndTime",
        matchKey: "itemEndTime",
        label: "Kortast tid kvar",
        sortOrder: "asc",
        sortBy: "itemEndTime",
      },
      {
        id: "itemEndTimeDesc",
        queryKey: "itemEndTime",
        matchKey: "itemEndTime",
        label: "Nyast",
        sortOrder: "desc",
        sortBy: "itemEndTime",
      },
      {
        id: "itemSortNoAsc",
        queryKey: "itemSortNo",
        matchKey: "itemSortNo",
        label: "Rop",
        sortOrder: "asc",
        sortBy: "itemSortNo",
      },
    ],
    selections: [
      {
        matchKey: "itemEndTime",
        queryKey: "itemEndTime",
        sortOrder: "asc",
        sortBy: "itemEndTime",
      },
    ],
  },
  filtering: {
    selections: [
      {
        queryKey: "isOngoing",
        matchKey: "isOngoing",
        value: "active",
      },
    ],
    options: [
      {
        queryKey: "isOngoing",
        matchKey: "isOngoing",
        label: "Pågående",
        value: "active",
      },
      {
        queryKey: "hasEnded",
        matchKey: "hasEnded",
        label: "Avslutade",
        value: "ended",
      },
      {
        queryKey: "bidWon",
        matchKey: "bidWon",
        label: "Vunna",
        value: "yes",
      },
    ],
  },
};

const BidsAndFavorites = ({ block, blockStyle, extraClass }: BlockProps) => {
  const auctionFilterProps = useAuctionFilter(
    defaultFilterPropsMyPagesAuctionItem
  );
  const [userAuctionItemBids, setUserAuctionItemBids] = useState<any>(false);

  const { options, setOptions, buildFilterParams } = auctionFilterProps;

  const paginationArgs = options?.pagination as any;
  const sortingArgs = options?.sorting as any;

  const { user, userId, setFetchKeys, fetchKeys } = useUserStore();

  // Generate a unique fetch key on every mount — synchronous, so SWR sees it on the first render
  const [userFetchKey] = useState(() => MD5(`userBids-${userId}-${Date.now()}`).toString());

  // Exclude pagination from the SWR key — the API returns all items regardless of offset/limit,
  // so page changes should not trigger a new fetch; we paginate client-side from the cached data.
  const filterSortJson = JSON.stringify({
    filtering: options?.filtering,
    sorting: options?.sorting,
  });

  const fetchUserItems = async () => {
    const filter = buildFilterParams();

    const params = {
      sort: {
        [sortingArgs.selections?.[0]?.queryKey]:
          sortingArgs.selections?.[0]?.sortOrder,
      },
      filter: filter,
    };

    const { auctionItems: getAllAuctionItems, totalHits: getTotalHits } =
      await getUserAuctionItems(params, userFetchKey);

    if (!getAllAuctionItems) return [];

    setOptions((prevArgs: auctionFilterProps) => ({
      ...prevArgs,
      pagination: {
        ...prevArgs.pagination,
        total: getTotalHits ? getTotalHits : 0,
      },
    }));

    return getAllAuctionItems;
  };

  const {
    data: userItems,
    error,
    isLoading,
    isValidating,
  } = useSWR<AuctionItem[], Error>(
    ["api/getUserBidAndFavorites", filterSortJson, userFetchKey],
    fetchUserItems,
    {
      refreshInterval: REVALIDATE_USER_AUCTIONS_TIME,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      shouldRetryOnError: true,
    }
  );

  // Paginate client-side — API ignores offset/limit and returns all items
  const displayedItems = useMemo(() => {
    if (!userItems?.length) return [];
    const pageOffset = (paginationArgs.page - 1) * paginationArgs.size;
    return userItems.slice(pageOffset, pageOffset + paginationArgs.size);
  }, [userItems, paginationArgs.page, paginationArgs.size]);

  // Fetch bids only for the items on the current page
  const auctionItemIds = displayedItems.map((item: any) => item.itemId);
  const { data: bidsData } = useSWR(
    displayedItems.length > 0
      ? ["api/getUserItemBids", auctionItemIds.join(","), userFetchKey]
      : null,
    async () => {
      const { allItemBids } = await getRecentItemBidsByItemIds(auctionItemIds, userFetchKey);
      return allItemBids;
    },
    {
      refreshInterval: REVALIDATE_USER_AUCTIONS_TIME,
      revalidateOnFocus: false,
      dedupingInterval: 5000,
    }
  );

  // Update bids when data changes
  useEffect(() => {
    if (bidsData) {
      setUserAuctionItemBids(bidsData);
    }
  }, [bidsData]);

  return (
      <div className="flex flex-col space-y-6 w-full" style={blockStyle}>
        <AuctionBarActionsPanelMyPages filterProps={auctionFilterProps} />

        {(isLoading || (isValidating && !displayedItems?.length)) ? (
          <div className="space-y-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rounded-lg overflow-hidden">
                <Skeleton height={300} />
              </div>
            ))}
          </div>
        ) : (
          <AuctionListItems
            visibleItemsData={displayedItems?.length}
            auctionItemBids={userAuctionItemBids}
            filterProps={auctionFilterProps}
            auctionItems={displayedItems}
            isLoading={isLoading}
            error={error}
            showFilter={false}
            searchLayout
          />
        )}
      </div>
  );
};

export default BidsAndFavorites;
